// @ts-nocheck
// import LoadingSpinner from "@insite/mobius/LoadingSpinner"; // Starter Custom Code
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import DisablerContext from "@insite/mobius/utilities/DisablerContext";
import getProp from "@insite/mobius/utilities/getProp";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import safeColor from "@insite/mobius/utilities/safeColor";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
// import styled, { withTheme } from "styled-components"; // Starter Custom Code

/* Starter Custom Code - Start */
import BaseLoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import styled, { css, ThemeProps, withTheme } from "styled-components";

type LoadingOverlayStyleProps = Pick<LoadingOverlayProps, "css" | "minHeight"> & {
    isLoading?: LoadingOverlayProps["loading"];
    size?: LoadingSpinnerProps["size"];
};

const LoadingSpinner = styled(BaseLoadingSpinner)`
    position: relative;
    ${({ size }) => `
        top: -${(size || 40) / 2}px;
        left: -${(size || 40) / 2}px;
    `}
`;

const Wrapper = styled.div`
    max-height: 99.9%; /* Fix an overflow issue */
`;
/* Starter Custom Code - End */

interface LoadingOverlayPresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<LoadingOverlayProps>;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
}

export type LoadingOverlayProps = MobiusStyledComponentProps<
    "div",
    LoadingOverlayPresentationProps & {
        /** CSS string or styled-components function to be injected into this component. */
        /** Show/hide status of the backdrop and spinner. */
        loading?: boolean;
        /** Props to be passed to the inner LoadingSpinner component. */
        spinnerProps?: LoadingSpinnerProps; // Starter Custom Code
        // Starter Custom Code
        /** min-height of the container when the spinner is visible */
        minHeight?: number | null;
    }
>;

// Starter Custom - next line - LoadingOverlayStyleProps
const LoadingOverlayStyle = styled.div<LoadingOverlayStyleProps>`
    display: block; /* Starter Custom Code */
    position: relative;
    width: 100%;
    & > div:first-child {
        height: 100%;
        width: 100%;
        background: ${props => safeColor(props.theme.colors.common.background).rgb().alpha(0.6).string()};
        will-change: opacity;
        opacity: 0;
        transition: opacity 0.2s ease-in-out;
        ${injectCss}
        &[data-loading="true"] {
            opacity: 1;
            position: absolute;
            z-index: ${getProp("theme.zIndex.loadingOverlay")};
            /* Starter Custom Code - Start */
            top: 0;
            left: 0;
            /* Starter Custom Code - End */
        }
        &[data-loading="false"] {
            position: fixed;
            display: none;
        }
    }
    /* Starter Custom Code - Start */
    ${({ size, minHeight, isLoading, theme }) => {
        if (isLoading) {
            return css`
                min-height: ${minHeight || size || theme.loadingSpinner.defaultProps?.size || 40}px;
            `;
        }
    }}
    /* Starter Custom Code - End */
    ${injectCss}
`;

const SpinnerWrapper = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    right: 50%;
    bottom: 50%;
    margin: auto;
`;

/**
 * Adds a layer over the content to indicate that it is loading.
 */
const LoadingOverlay: React.FC<LoadingOverlayProps> = withTheme(
    ({ children, mergeCss, loading = true, spinnerProps, theme, ...otherProps }) => {
        const { applyStyledProp } = applyPropBuilder(
            { children, mergeCss, loading, spinnerProps, theme, ...otherProps },
            { component: "loadingOverlay" },
        );
        const resolvedMergeCss = mergeCss ?? otherProps?.theme?.loadingOverlay?.defaultProps?.mergeCss;

        return (
            <LoadingOverlayStyle
                css={applyStyledProp("css", resolvedMergeCss)}
                {...otherProps}
                // Starter Custom Code
                isLoading={loading}
                size={spinnerProps?.size}
            >
                {/* Starter Custom Code - next-line - Wrapper */}
                <Wrapper
                    data-loading={loading}
                    data-test-selector="loadingOverlaySpinner"
                    role="alert"
                    aria-live="assertive"
                >
                    <VisuallyHidden>
                        {loading ? theme.translate("loading content") : theme.translate("content loaded")}
                    </VisuallyHidden>
                    <SpinnerWrapper>
                        <LoadingSpinner {...spinnerProps} />
                    </SpinnerWrapper>
                </Wrapper>
                <DisablerContext.Provider value={{ disable: loading }}>{children}</DisablerContext.Provider>
            </LoadingOverlayStyle>
        );
    },
);

/** @component */
export default LoadingOverlay;

export { LoadingOverlayStyle };
